import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, EyeOff, History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { refreshRed, useRedEstado } from "@/lib/red";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/common/DetailFields";
import { Switch } from "@/components/ui/switch";
import { MemberAvatar } from "@/components/red/MemberAvatar";

const Blocked = () => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/red/bloqueados").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const unblock = async (m) => {
    try { await api.post("/red/conexiones/desbloquear", { user_id: m.id }); toast.success(`Desbloqueaste a ${m.nombre_publico}. Puedes volver a pedir la conexión.`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items) return null;
  return (
    <div className="space-y-1.5" data-testid="privacy-blocked-list">
      <p className="text-sm font-semibold">Bloqueados</p>
      {items.length === 0 ? <p data-testid="privacy-blocked-empty" className="text-xs c-muted">No tienes miembros bloqueados.</p> : items.map((m) => (
        <div key={m.id} data-testid="privacy-blocked-item" className="flex items-center gap-2.5 py-1">
          <MemberAvatar m={m} size="w-8 h-8 text-xs" />
          <span className="flex-1 text-sm truncate">{m.nombre_publico}</span>
          <button data-testid="privacy-unblock-btn" onClick={() => unblock(m)} className="text-xs font-semibold c-accent px-2 py-1 rounded-lg border c-line">Desbloquear</button>
        </div>
      ))}
    </div>
  );
};

const LinkRow = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-2.5 py-2 text-sm font-semibold"><Icon className="w-4 h-4 c-accent" /><span className="flex-1">{label}</span><ChevronRight className="w-4 h-4 c-muted" /></Link>
);

export const RedPrivacySection = () => {
  const { isOwner } = useAuth();
  const [e] = useRedEstado();
  const toggle = async (v) => {
    try { await api.patch("/red/privacidad", { modo_privado: v }); await refreshRed(); toast.success(v ? "Modo privado activado" : "Modo privado desactivado"); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!e) return null;
  return (
    <Section title="Privacidad de la red" testid="config-red-privacy">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold">Modo privado</p>
            <p className="text-xs c-text-2">Tu cuenta no aparece en resultados de nadie, no sirve de conector entre otros miembros, y no puedes buscar ni pedir presentaciones mientras esté activo.{!isOwner && " Solo el dueño de la cuenta lo cambia."}</p>
          </div>
          <Switch data-testid="privacy-private-mode-switch" checked={e.modo_privado} disabled={!isOwner} onCheckedChange={toggle} />
        </div>
        <Blocked />
        <div className="border-t c-line pt-1">
          <LinkRow to="/conecta/actividad" icon={History} label="Mi actividad en la red" testid="privacy-activity-link" />
          <LinkRow to="/conecta/red?vista=no_compartidos" icon={EyeOff} label={`Contactos no compartidos${e.no_compartidos ? ` (${e.no_compartidos})` : ""}`} testid="privacy-not-shared-link" />
        </div>
      </div>
    </Section>
  );
};
